import { IntlKey } from 'locales';
import { isEmpty } from 'lodash-es';
import { FieldMetadata } from 'models/forms/fields';
import { FieldInput, getFieldConfigByMetadataType } from 'utils/import/fieldMetadata/fieldConfig';
import { ImportFormRequest, ImportValidationError } from 'utils/import/import.models';
import { defaultValidator } from 'utils/import/validators/defaultValidator';

import { DataMap } from './parseFileUtils';

type TranslationMap = Record<IntlKey, string>;
type FieldMetadataMap = Record<string, FieldMetadata>;

type FieldValidationResult = {
  isValid: boolean;
  parsedValue?: unknown;
  errorMsg?: IntlKey;
  shouldSkip?: boolean;
};

type FieldProcessingResult = {
  variationParameter?: { id: string; value: unknown };
  validationError?: ImportValidationError;
};

export type ImportedProductRow = {
  productType: string;
  productSubType: string;
  wrapper: string;
  dataMap: DataMap;
  fieldMetadataMap?: FieldMetadataMap;
};

/**
 * Validates and parses a field value using the provided field metadata
 */
function validateAndParseField(
  value: FieldInput,
  fieldMetadata: FieldMetadata,
  translationMap: TranslationMap,
): FieldValidationResult {
  const { parser, validator } = getFieldConfigByMetadataType(fieldMetadata, translationMap);

  if (!parser) {
    return { isValid: false, shouldSkip: true };
  }

  const parsed = parser(value);
  const validation = validator ? validator(parsed) : defaultValidator(parsed);

  if (validation.isValid) {
    return { isValid: true, parsedValue: validation.value };
  } else {
    return {
      isValid: false,
      errorMsg: validation.errorMsgs?.[0] || 'importFileValidator.validationError',
    };
  }
}

/**
 * Processes a single field entry from the File data
 */
function processImportedField(
  label: string,
  value: FieldInput,
  fieldMetadataMap: FieldMetadataMap,
  translationMap: TranslationMap,
  rowIndex: number,
): FieldProcessingResult {
  const fieldMetadata = fieldMetadataMap[label];

  // Skip if value is empty, field metadata is missing, or field is hidden
  if ((typeof value === 'string' && isEmpty(value.trim())) || value === undefined || isEmpty(fieldMetadata)) {
    return {};
  }

  const parseResult = validateAndParseField(value, fieldMetadata, translationMap);

  // Skip field if no parser is available
  if (parseResult.shouldSkip) {
    return {};
  }

  if (parseResult.isValid && parseResult.parsedValue !== undefined) {
    return {
      variationParameter: {
        id: fieldMetadata.id,
        value: parseResult.parsedValue,
      },
    };
  } else if (!parseResult.isValid) {
    return {
      validationError: {
        row: rowIndex,
        fieldId: fieldMetadata.id,
        errorMsg: parseResult.errorMsg || 'importFileValidator.validationError',
      },
    };
  }

  return {};
}

/**
 * Processes a single File row and returns the import form request
 */
export function buildImportRequest(
  productRow: ImportedProductRow,
  rowIndex: number,
  productFieldMetadataMap: Record<string, Record<string, FieldMetadataMap>>,
  translationMap: TranslationMap,
): ImportFormRequest {
  const { productType, productSubType, wrapper, dataMap } = productRow;
  const fieldMetadataMap = productFieldMetadataMap[productType]?.[productSubType] || {};

  const variationParameters: Record<string, unknown> = {};
  const validationErrors: ImportValidationError[] = [];

  Object.entries(dataMap).forEach(([label, value]) => {
    const result = processImportedField(label, value, fieldMetadataMap, translationMap, rowIndex);

    if (result.variationParameter) {
      variationParameters[result.variationParameter.id] = result.variationParameter.value;
    }

    if (result.validationError) {
      validationErrors.push(result.validationError);
    }
  });

  return {
    productType,
    productSubType,
    wrapper,
    variationParameters,
    validationErrors,
  };
}
